import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { detectAndProcessERP } from './parsers/index';
import { getBaseUrl } from '../useFileImport';

const getMicroServiceUrl = () => `${getBaseUrl()}/api/process`;

// Lê um arquivo e retorna a matriz 2D (header:1)
const readFileToArray = async (file) => {
    if (file.name.toLowerCase().endsWith('.csv')) {
        const buffer = await file.arrayBuffer();
        let text;
        try {
            text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
        } catch (e) {
            text = new TextDecoder('iso-8859-1').decode(buffer);
        }
        const lines = text.split(/\r?\n/);
        const firstLine = lines[0] || '';
        const delimiter = firstLine.split(';').length >= firstLine.split(',').length ? ';' : ',';
        return lines.map(line => line.split(delimiter).map(cell => cell.trim()));
    } else {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { raw: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        return XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
    }
};

// Normaliza data para YYYY-MM-DD
const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    // DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
        const [d, m, y] = s.split('/');
        return `${y}-${m}-${d}`;
    }
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        return s.substring(0, 10);
    }
    return s;
};

export const useSalesAudit = () => {
    const [erpData, setErpData] = useState(null);
    const [erpName, setErpName] = useState('');
    const [operatorFiles, setOperatorFiles] = useState([]);
    const [auditResult, setAuditResult] = useState(null);
    const [loading, setLoading] = useState(false);

    // Upload do arquivo do sistema interno (ERP)
    const uploadERP = useCallback(async (file) => {
        setLoading(true);
        try {
            const dadosArray = await readFileToArray(file);
            const { vendas, nomeERP } = detectAndProcessERP(dadosArray);

            if (!vendas || vendas.length === 0) {
                throw new Error('Nenhuma venda válida encontrada no relatório interno.');
            }

            setErpData(vendas);
            setErpName(nomeERP);
            setAuditResult(null);
            return { count: vendas.length, name: nomeERP };
        } catch (error) {
            console.error('Erro ao processar ERP:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Adicionar arquivo de maquininha (envia ao micro-serviço)
    const addOperatorFile = useCallback(async (file) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('fileType', 'auditoria');

            const response = await fetch(getMicroServiceUrl(), {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Erro ao processar arquivo da maquininha');
            }

            const vendas = result.data || [];
            if (vendas.length === 0) {
                throw new Error('Nenhuma venda encontrada no arquivo da maquininha.');
            }

            // Detectar operadora pelo metadata do micro-serviço ou pelo nome do arquivo
            const operatorName = result.metadata?.operator || result.detectedType || file.name.split('.')[0];

            // Normalizar: extrair apenas data e valor
            const vendasNormalizadas = vendas.map((v, idx) => {
                let valorStr = String(v.valor).replace(/[R$\s]/g, '').trim();
                // Remove pontos de milhar e troca vírgula por ponto decimal
                if (valorStr.includes(',') && valorStr.includes('.')) {
                    valorStr = valorStr.replace(/\./g, '').replace(',', '.');
                } else if (valorStr.includes(',')) {
                    valorStr = valorStr.replace(',', '.');
                }
                const valorFinal = parseFloat(valorStr) || 0;

                return {
                    data: normalizeDate(v.data_venda),
                    valor: valorFinal,
                    bandeira: v.bandeira || '',
                    categoria: v.categoria || '',
                    tipo: v.tipo || '',
                    linha: idx + 1,
                };
            });

            const novoArquivo = {
                fileName: file.name,
                operatorName: operatorName,
                sales: vendasNormalizadas,
                count: vendasNormalizadas.length,
            };

            setOperatorFiles(prev => [...prev, novoArquivo]);
            setAuditResult(null);
            return novoArquivo;
        } catch (error) {
            console.error('Erro ao processar arquivo da maquininha:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Remover um arquivo de operadora
    const removeOperatorFile = useCallback((index) => {
        setOperatorFiles(prev => prev.filter((_, i) => i !== index));
        setAuditResult(null);
    }, []);

    // Motor de cruzamento (Data + Valor, 1-para-1)
    const runAudit = useCallback(() => {
        if (!erpData || erpData.length === 0) {
            throw new Error('Nenhum dado do sistema interno carregado.');
        }
        if (operatorFiles.length === 0) {
            throw new Error('Nenhum arquivo de maquininha carregado.');
        }

        // Consolida todas as vendas das maquininhas num único array
        const todasMaquininhas = [];
        operatorFiles.forEach(opFile => {
            opFile.sales.forEach(sale => {
                todasMaquininhas.push({
                    ...sale,
                    origin: opFile.operatorName,
                    fileName: opFile.fileName,
                });
            });
        });

        const matched = [];
        const unmatchedERP = [];
        const maquininhaUsada = new Set();

        // Para cada venda do ERP, procura correspondente nas maquininhas
        erpData.forEach(erpSale => {
            const erpDate = normalizeDate(erpSale.data);
            const erpValor = parseFloat(erpSale.valor) || 0;

            const matchIndex = todasMaquininhas.findIndex((maq, index) => {
                return !maquininhaUsada.has(index) &&
                       normalizeDate(maq.data) === erpDate &&
                       Math.abs(maq.valor - erpValor) < 0.01 &&
                       (!erpSale.categoria || !maq.categoria || erpSale.categoria === maq.categoria);
            });

            if (matchIndex !== -1) {
                maquininhaUsada.add(matchIndex);
                matched.push({
                    erp: erpSale,
                    maquininha: todasMaquininhas[matchIndex],
                    status: 'encontrado',
                });
            } else {
                unmatchedERP.push({
                    erp: erpSale,
                    status: 'sem_maquininha',
                });
            }
        });

        // Vendas que estão nas maquininhas mas NÃO no ERP
        const unmatchedOperator = [];
        todasMaquininhas.forEach((maq, index) => {
            if (!maquininhaUsada.has(index)) {
                unmatchedOperator.push({
                    maquininha: maq,
                    status: 'sem_erp',
                });
            }
        });

        // Calcular totais
        const totalERP = erpData.reduce((sum, s) => sum + (parseFloat(s.valor) || 0), 0);
        const totalMaquininhas = todasMaquininhas.reduce((sum, s) => sum + s.valor, 0);
        const totalMatched = matched.reduce((sum, m) => sum + (parseFloat(m.erp.valor) || 0), 0);
        const totalUnmatchedERP = unmatchedERP.reduce((sum, u) => sum + (parseFloat(u.erp.valor) || 0), 0);
        const totalUnmatchedOp = unmatchedOperator.reduce((sum, u) => sum + u.maquininha.valor, 0);

        const resultado = {
            matched,
            unmatchedERP,
            unmatchedOperator,
            summary: {
                totalERP,
                totalMaquininhas,
                totalMatched,
                totalUnmatchedERP,
                totalUnmatchedOp,
                countERP: erpData.length,
                countMaquininhas: todasMaquininhas.length,
                countMatched: matched.length,
                countUnmatchedERP: unmatchedERP.length,
                countUnmatchedOp: unmatchedOperator.length,
                percentMatched: erpData.length > 0 ? (matched.length / erpData.length) * 100 : 0,
                bate100: unmatchedERP.length === 0 && unmatchedOperator.length === 0,
            },
        };

        setAuditResult(resultado);
        return resultado;
    }, [erpData, operatorFiles]);

    // Limpar tudo
    const clearAll = useCallback(() => {
        setErpData(null);
        setErpName('');
        setOperatorFiles([]);
        setAuditResult(null);
    }, []);

    return {
        erpData,
        erpName,
        operatorFiles,
        auditResult,
        loading,
        uploadERP,
        addOperatorFile,
        removeOperatorFile,
        runAudit,
        clearAll,
    };
};
